// Shared helpers for the post-authentication redirect-back flow.
//
// Two channels feed the LoginPage / RegisterPage resolver:
//   1. React Router `location.state.from` — the idiomatic, in-memory
//      hand-off used by ProtectedRoute and by in-app navigations
//      (SalonDetailPage, etc.).
//   2. sessionStorage — a one-shot fallback for the 401 interceptor in
//      api.js, which can't set Router state because it lives outside
//      the component tree and needs a hard `window.location.href`
//      navigation to clear auth state cleanly.
//
// The open-redirect guard in isSafeInternalPath is not optional: an
// attacker-controlled "from" value like "//evil.example" or
// "https://evil.example" would, without this, happily bounce a
// freshly-authenticated user out of the app right after they logged in.

const POST_AUTH_REDIRECT_KEY = '__petzy_post_auth_redirect';

export function isSafeInternalPath(path) {
  if (typeof path !== 'string') return false;
  if (!path.startsWith('/')) return false;        // must be absolute path
  if (path.startsWith('//')) return false;        // protocol-relative URL
  if (/:\/\//.test(path)) return false;           // absolute URL sneaked in
  // Also reject control characters / newlines that could confuse a
  // downstream router or a URL parser embedded in analytics hooks.
  if (/[\r\n\t]/.test(path)) return false;
  return true;
}

// Terminal/ephemeral pages that render EXCLUSIVELY from in-memory router
// state. Navigation state does not survive a logout/login round-trip, so
// bouncing back to them lands on their bare empty-state shell (seen in
// prod: client books → logs out on the confirmation page → a background
// 401 stashes the path → the NEXT account to log in gets teleported to
// "Nu s-au găsit detalii despre programare"). The booking itself lives
// in /my-bookings; fall through to the role-based default instead.
const EPHEMERAL_STATE_PATHS = new Set(['/booking/confirmation', '/booking/payment']);

function isResumablePath(path) {
  if (!isSafeInternalPath(path)) return false;
  // Strip the query/hash, then trailing slashes, before the set check.
  // React Router v6 matches "/booking/confirmation/" to the same route as
  // "/booking/confirmation", so without normalizing a stashed trailing-
  // slash path would slip past EPHEMERAL_STATE_PATHS and resume onto the
  // bare empty-state shell anyway. Root "/" is length 1 and kept as-is.
  const base = path.split(/[?#]/)[0];
  const normalized = base.length > 1 ? base.replace(/\/+$/, '') : base;
  return !EPHEMERAL_STATE_PATHS.has(normalized);
}

/**
 * Given a React Router `location.state` (may be null), resolve the
 * post-auth redirect path — preferring in-memory state, falling back to
 * the sessionStorage handoff. Returns `fallback` if neither channel
 * carries a safe, resumable internal path.
 */
export function resolvePostAuthRedirect(locationState, fallback = '/') {
  const fromState = locationState?.from;
  if (fromState && isResumablePath(fromState)) return fromState;
  try {
    const fromSession = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
    if (fromSession && isResumablePath(fromSession)) return fromSession;
  } catch { /* sessionStorage unavailable */ }
  return fallback;
}

/**
 * Stash a path for the sessionStorage-backed fallback channel. Used by
 * the 401 interceptor and by any non-router context that wants to bounce
 * the user through login. Invalid paths are silently dropped rather than
 * thrown — the caller may be in a catch-all error flow where a throw
 * would mask a different error.
 */
export function stashPostAuthRedirect(path) {
  if (!isSafeInternalPath(path)) return;
  try { sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, path); }
  catch { /* noop */ }
}

/**
 * Clear the sessionStorage handoff. Called from LoginPage / RegisterPage
 * after a successful auth so a stale value doesn't bounce the next
 * legitimate login somewhere unexpected.
 */
export function clearPostAuthRedirect() {
  try { sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY); }
  catch { /* noop */ }
}
